import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../services/stripeService';
import PaymentForm from './PaymentForm';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  IconButton,
  Avatar,
  CloseButton
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';

const PaymentInitiateModal = ({ companyId, onSuccess, onClose }) => {
  const [step, setStep] = useState(1);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock data - replace with actual API calls
  useEffect(() => {
    const fetchCompletedProjects = async () => {
      setLoading(true);
      // TODO: Replace with actual API call to get students with completed projects
      const mockStudents = [
        {
          id: 'student1',
          name: 'Sarah Johnson',
          avatar: '',
          project: {
            id: 'proj1',
            title: 'E-commerce Website',
            agreedAmount: 150000 // $1500 in cents
          }
        },
        {
          id: 'student2', 
          name: 'Mike Chen',
          avatar: '',
          project: {
            id: 'proj2',
            title: 'Mobile App Design',
            agreedAmount: 120000 // $1200 in cents
          }
        },
        {
          id: 'student3',
          name: 'Emma Williams',
          avatar: '',
          project: {
            id: 'proj3', 
            title: 'Marketing Dashboard',
            agreedAmount: 80000 // $800 in cents
          }
        }
      ];
      
      setTimeout(() => {
        setStudents(mockStudents);
        setLoading(false);
      }, 500);
    };

    fetchCompletedProjects();
  }, [companyId]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSelectedProject(student.project);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 1) {
      onClose();
    } else if (step === 2) {
      setStep(1);
      setSelectedStudent(null);
      setSelectedProject(null);
    }
  };

  const formatAmount = (amountInCents) => {
    return `$${(amountInCents / 100).toFixed(2)}`;
  };

  const handlePaymentSuccess = (paymentResult) => {
    // TODO: Save payment reason to database if half payment
    onSuccess(paymentResult);
  };

  const modal = (
    <Box
      position="fixed"
      inset={0}
      backgroundColor="rgba(0, 0, 0, 0.75)"
      backdropFilter="blur(8px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={9999}
    >
      <Box
        bg="white"
        borderRadius="16px"
        maxW="800px"
        w="95%"
        maxH="90vh"
        overflow="hidden"
        boxShadow="0 10px 30px rgba(0,0,0,0.2)"
      >
        {/* Header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          p={8}
          borderBottom="2px solid"
          borderColor="#eef1ff"
        >
          <HStack spacing={3}>
            {step > 1 && (
              <IconButton
                icon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="ghost"
                size="sm"
                color="#6c63ff"
              />
            )}
            <Heading size="lg" color="#6c63ff">
              {step === 1 && 'Choose Student to Pay'}
              {step === 2 && `Pay ${selectedStudent?.name}`}
            </Heading>
          </HStack>
          <CloseButton onClick={onClose} size="lg" />
        </Flex>

        {/* Content */}
        <Box p={8} maxH="70vh" overflowY="auto">
          {/* Step 1: Student Selection */}
          {step === 1 && (
            <VStack spacing={4} align="stretch">
              {loading ? (
                <Text textAlign="center" py={10} color="#6c63ff">
                  Loading students with completed projects...
                </Text>
              ) : students.length === 0 ? (
                <Box textAlign="center" py={10}>
                  <Text fontSize="lg" mb={2}>No completed projects found</Text>
                  <Text color="#666">Students need to complete projects before you can pay them.</Text>
                </Box>
              ) : (
                students.map((student) => (
                  <Box
                    key={student.id}
                    p={6}
                    borderRadius="12px"
                    border="2px solid"
                    borderColor="#eef1ff"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{
                      borderColor: "#6c63ff",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(108, 99, 255, 0.15)"
                    }}
                    onClick={() => handleStudentSelect(student)}
                  >
                    <Flex alignItems="center" gap={4}>
                      <Avatar
                        name={student.name}
                        src={student.avatar}
                        size="lg"
                        bg="#6c63ff"
                        color="white"
                      />
                      <Box flex={1}>
                        <Text fontWeight="bold" fontSize="xl">
                          {student.name}
                        </Text>
                        <Text color="#666" fontSize="md">
                          {student.project.title}
                        </Text>
                        <Text color="#6c63ff" fontWeight="500" fontSize="md">
                          Agreed Amount: {formatAmount(student.project.agreedAmount)}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))
              )}
            </VStack>
          )}

          {/* Step 2: PayPal-style Payment */}
          {step === 2 && selectedStudent && (
            <VStack spacing={8} align="stretch">
              {/* PayPal-style header */}
              <Box 
                bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)" 
                borderRadius="16px" 
                p={12} 
                textAlign="center"
                color="white"
                position="relative"
                overflow="hidden"
              >
                <Box position="relative" zIndex={1}>
                  <Avatar
                    name={selectedStudent.name}
                    src={selectedStudent.avatar}
                    size="2xl"
                    mb={4}
                    border="4px solid white"
                    boxShadow="0 8px 32px rgba(0,0,0,0.3)"
                  />
                  <Heading size="xl" mb={3} fontWeight="bold">
                    {selectedProject.title}
                  </Heading>
                  <Text opacity={0.9} fontSize="lg">
                    Student: {selectedStudent.name}
                  </Text>
                </Box>
                {/* Decorative elements */}
                <Box
                  position="absolute"
                  top="-50px"
                  right="-50px"
                  width="200px"
                  height="200px"
                  borderRadius="50%"
                  bg="rgba(255,255,255,0.1)"
                />
                <Box
                  position="absolute"
                  bottom="-30px"
                  left="-30px"
                  width="150px"
                  height="150px"
                  borderRadius="50%"
                  bg="rgba(255,255,255,0.05)"
                />
              </Box>

              {/* Payment amount */}
              <Box textAlign="center" py={6}>
                <Text fontSize="md" color="#666" mb={3} textTransform="uppercase" letterSpacing="wide">
                  Send Money to {selectedProject.title}
                </Text>
                <Text fontSize="6xl" fontWeight="bold" color="#2d3748">
                  {formatAmount(selectedProject.agreedAmount)}
                </Text>
                <Text fontSize="lg" color="#666" mt={2}>
                  USD
                </Text>
              </Box>

              {/* Stripe Payment Form */}
              <Elements stripe={getStripe()}>
                <PaymentForm
                  companyId={companyId}
                  studentId={selectedStudent.id}
                  projectId={selectedProject.id}
                  prefilledAmount={(selectedProject.agreedAmount / 100).toString()}
                  prefilledDescription={`Payment for ${selectedProject.title}`}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleBack}
                />
              </Elements>
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );

  return createPortal(modal, document.body);
};

export default PaymentInitiateModal; 